/**
 * Users API - uses Axios apiClient with auth interceptors.
 * authorization-role and authorization-resource are injected by the request interceptor from authStore.
 */
import { apiClient } from "features/shared/api/axiosInstance";
import type { PaginatedUsersDto } from "common/models/api/models/PaginatedUsersDto";
import type { UserDto } from "common/models/api/models/UserDto";
import type { UserKpisDto } from "common/models/api/models/UserKpisDto";
import type { RangeStatsDto } from "common/models/api/models/RangeStatsDto";
import type { CreateUserRequest } from "common/models/api/models/CreateUserRequest";
import type { CreateVerificationTokenRequest } from "common/models/api/models/CreateVerificationTokenRequest";
import type { UpdateUserRequest } from "common/models/api/models/UpdateUserRequest";
import type { InviteUserProgramRequest } from "common/models/api/models/InviteUserProgramRequest";
import type { InviteUserOrganizationRequest } from "common/models/api/models/InviteUserOrganizationRequest";
import type { BlockUnblockUserRequest } from "common/models/api/models/BlockUnblockUserRequest";

type RoleType = "organization-owner" | "operation-manager" | "program-manager" | "partner" | "customer-service";

export const usersApi = {
    findAll: (params: {
        page?: number;
        pageSize?: number;
        query?: string;
        order?: "ASC" | "DESC";
        orderBy?: string;
        roleFilter?: RoleType;
    }) => apiClient.get<PaginatedUsersDto>("/api/users", { params }),

    info: (authorizationResource?: string, authorizationRole?: string) =>
        apiClient.get<UserDto>("/api/users/info", {
            headers: {
                "authorization-resource": authorizationResource,
                "authorization-role": authorizationRole,
            },
        }),

    kpis: () => apiClient.get<UserKpisDto>("/api/users/kpis"),

    getUsersByDay: (authorizationResource?: string, numberOfSamples?: number, authorizationRole?: string) =>
        apiClient.get<RangeStatsDto>("/api/users/users-by-day", {
            params: { numberOfSamples },
            headers: {
                "authorization-resource": authorizationResource,
                "authorization-role": authorizationRole,
            },
        }),

    create: (body: CreateUserRequest) => apiClient.post<UserDto>("/api/users", body),

    updateUser: (body: UpdateUserRequest) => apiClient.put("/api/users", body),

    updateUserById: (id: number, body: UpdateUserRequest) => apiClient.put(`/api/users/${id}`, body),

    block: (body: BlockUnblockUserRequest) => apiClient.post("/api/users/block", body),

    unblock: (body: BlockUnblockUserRequest) => apiClient.post("/api/users/unblock", body),

    inviteUserToProgram: (body: InviteUserProgramRequest) => apiClient.post<UserDto>("/api/users/invite-to-program", body),

    inviteUserToOrganization: (body: InviteUserOrganizationRequest) => apiClient.post("/api/users/invite-to-organization", body),

    resendVerificationToken: (body: CreateVerificationTokenRequest) => apiClient.post("/api/users/resend-verification-token", body),

    export: (params?: { query?: string; order?: "ASC" | "DESC"; orderBy?: string; roleFilter?: string }) =>
        apiClient.get<string>("/api/users/export", { params }),

    validateMigratedUser: (identificationCode: string, apiKeyOrq: string) =>
        apiClient.get<{ migrated: boolean }>("/api/users/validate-migrated-user", {
            params: { identificationCode, apiKeyOrq },
        }),
};
